import {
  Box,
  Button,
  Card,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  Typography,
} from '@mui/material';
import { Row } from './Row';
import { isEmpty } from 'lodash';
import { useMemo, useState } from 'react';
import { Add } from '@mui/icons-material';
import useTable from 'src/hooks/useTable';
import { AppointmentForm } from './AppointmentForm';
import { TableHeadCustom } from 'src/components/table';
import { useAppointments } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { TABLE_HEAD_APPOINTMENT } from 'src/careflow/common/table-head';
import {
  AppointmentWrapper,
  WrappedAppointment,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Appointment';
import { v4 as uuidv4 } from 'uuid';
import { translateTableHead } from 'src/careflow/helpers/common';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { PermissionsProvider } from 'src/careflow/ph-core/admin/permissions/context/PermissionsContext';
import useComponentsConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useComponentsConfigData';

type Props = {
  patient: WrappedPatient | null;
  permissions: {
    isAllowedAdd: boolean;
    isAllowedEdit: boolean;
  };
};

export function AppointmentList({ patient, permissions }: Props) {
  const { i18n } = useLocales();

  const [add, setOpenAdd] = useState(false);
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({});
  const [
    appointments, { refresh: refreshAppointment },
  ] = useAppointments({
    filter: { patient: patient?.id },
    autofetch: !!patient?.id,
    map: AppointmentWrapper,
  });

  const componentsConfigData = useComponentsConfigData();

  const contextConfig = useMemo(() => {
    if (!componentsConfigData) {
      return;
    }

    // CHECK HERE the context of the form if needed to switch the config data

    return componentsConfigData.appointmentListConfig;
  }, [componentsConfigData]);

  const filteredTableHead = useMemo(() => {
    return TABLE_HEAD_APPOINTMENT.filter((item) => {
      if (contextConfig?.reasonTextColumn?.hide && item.id === 'reasonText') {
        return false;
      } else if (contextConfig?.reasonColumn?.hide && item.id === 'reason') {
        return false;
      }

      return true;
    });
  }, [TABLE_HEAD_APPOINTMENT, contextConfig]);

  return (
    <Card style={{ boxShadow: 'none', position: 'static' }} sx={{ m: 1 }}>
      <PermissionsProvider permissions={permissions}>
        <Grid container>
          <Grid item xs={12}>
            <TableContainer>
              <Stack sx={{ m: 0 }}>
                <Grid container display={'flex'} alignItems={'center'} paddingRight={2}>
                  {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.APPOINTMENT.ADD] }) && (
                    <Grid item xs={2} display={'flex'} justifyContent={'flex-end'}>
                      <Button
                        variant='contained'
                        size="small"
                        sx={{ height: '36px' }}
                        startIcon={<Add />}
                        style={{ position: 'absolute', top: '25px', right: '25px' }}
                        onClick={() => setOpenAdd(true)}
                      >
                        {i18n('patients.details.appointments.button', 'crs')}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Stack>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHeadCustom headLabel={translateTableHead(filteredTableHead, i18n, 'crs')} />
                <TableBody>
                  {!isEmpty(appointments) ? (
                    appointments
                      ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row: WrappedAppointment) => (
                        <Row
                          key={uuidv4()}
                          row={row}
                          patient={patient}
                          handleAppointment={refreshAppointment}
                        />
                      ))
                  ) : (
                    <TableCell colSpan={filteredTableHead?.length}>
                      <Typography variant="body2" align="center">
                        No rows
                      </Typography>
                    </TableCell>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
        <Box sx={{ position: 'relative' }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={appointments?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
          />
        </Box>
        <AppointmentForm
          patient={patient}
          appointment={null}
          open={add}
          onClose={() => setOpenAdd(false)}
          handleAppointment={refreshAppointment}
        />
      </PermissionsProvider>
    </Card>
  );
}
